
import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import JanesChatbot from './JanesChatbot'
import Sidebar from './Sidebar'
import Header from './Header'
import { Z_INDEX } from '@/lib/constants/z-index'

/**
 * @deprecated This component is deprecated as of 2025-10-20.
 * Use TanStack Router with _auth layout instead.
 *
 * Migration Guide:
 * 1. Create route file in src/routes/_auth/
 * 2. Remove DashboardLayout wrapper from page component
 * 3. Import page component in route file
 *
 * The _auth layout route automatically provides AuthLayout for all authenticated pages.
 * See AuthLayout component (src/layouts/AuthLayout.tsx) for the replacement pattern.
 *
 * @see docs/architecture/tanstack-router-migration-architecture.md
 *
 * Scheduled for removal: 2026-04-20 (6 months)
 */
interface DashboardLayoutProps {
  children: React.ReactNode
}

/**
 * @deprecated This component is deprecated as of 2025-10-20.
 * Use TanStack Router with _auth layout instead.
 *
 * Migration Guide:
 * 1. Create route file in src/routes/_auth/
 * 2. Remove DashboardLayout wrapper from page component
 * 3. Import page component in route file
 *
 * The _auth layout route automatically provides AuthLayout for all authenticated pages.
 * See AuthLayout component (src/layouts/AuthLayout.tsx) for the replacement pattern.
 *
 * @see docs/architecture/tanstack-router-migration-architecture.md
 *
 * Scheduled for removal: 2026-04-20 (6 months)
 */
export default function DashboardLayout({ children }: DashboardLayoutProps) {
  // Development-only deprecation warning
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[DEPRECATED] DashboardLayout is deprecated and will be removed on 2026-04-20.\n' +
        '\n' +
        'Use TanStack Router with _auth layout instead.\n' +
        '\n' +
        'Migration steps:\n' +
        '1. Create route file in src/routes/_auth/\n' +
        '2. Remove DashboardLayout wrapper from page component\n' +
        '3. Import page component in route file\n' +
        '\n' +
        'See: docs/architecture/tanstack-router-migration-architecture.md'
      )
    }
  }, [])
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [janesChatbotOpen, setJanesChatbotOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      // Redirect is now handled in the auth context
    } catch (error) {
      console.error('Error during logout:', error)
      // Still attempt to redirect even if there's an error
      window.location.href = '/login'
    }
  }

  return (
    <div className="min-h-screen bg-brand-dark-light flex relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 via-transparent to-brand-blue/5" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/50 to-transparent" />
      </div>

      {/* Sidebar - Always visible on desktop, toggleable on mobile */}
      <Sidebar 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        onJanesChatbotOpen={() => setJanesChatbotOpen(true)}
      />

      {/* Main Content */}
      <div className="flex-1 min-w-0 overflow-x-hidden lg:ml-0 relative z-10">
        {/* Header */}
        <Header 
          onMobileMenuOpen={() => setSidebarOpen(true)}
          user={user}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 min-w-0 overflow-x-hidden">
          <div className="animate-fade-in-up">
            {children}
          </div>
        </main>
      </div>

      {/* Sidebar Overlay - Enhanced with backdrop blur */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden animate-fade-in"
          style={{ zIndex: Z_INDEX.SIDEBAR_BACKDROP }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Janes Chatbot */}
      <JanesChatbot 
        isOpen={janesChatbotOpen} 
        onClose={() => setJanesChatbotOpen(false)} 
      />
    </div>
  )
} 